import { trpc } from "../../main/components/trpc.ts";
import classNames from "classnames";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import type { NextRdv } from "cadendar-shared";
import Select3 from "../../../components/Select3";
import Button from "../../main/components/Button";
import { pecMap, quoteMap, stateMap } from "./patientStateMaps.ts";
import { usePreferences } from "../../main/state/globalState";
import { openPatientFile } from "../openPatientFile";

interface PatientEnCoursLineProps {
  nom: string;
  prenom: string;
  state:
    | "toClassify"
    | "todo"
    | "done"
    | "apt_booked"
    | "must_call_back"
    | "must_be_called_back";
  quoteState:
    | "quote_sent"
    | "todo"
    | "quote_accepted"
    | "quote_refused"
    | "quote_abandoned"
    | "quote_created"
    | null;
  pecState:
    | "todo"
    | "waiting_for_answer"
    | "received"
    | "error"
    | "waiting_for_assurance_card"
    | null;
  patientId: string;
  stateId: number | null;
  comment: string;
  updated_at: Date | null;
  next_rdv: NextRdv | null;
  rowBackgroundClassName?: string;
  onPeekComment: ({ x, y }: { x: number; y: number }) => void;
  onHideComment: () => void;
  onEditComment: ({ x, y }: { x: number; y: number }) => void;
}

const PatientEnCoursLine = (props: PatientEnCoursLineProps) => {
  // const [comment, setComment] = useState(props.comment);
  const preferences = usePreferences();

  const utils = trpc.useUtils();
  const patientStateMutation = trpc.patient.commitPatientState.useMutation({
    onSettled: async () => {
      await utils.patient.getStatesByIds.invalidate();
      await utils.patient.getPatientsEnCours.invalidate();
    },
    onError: () => {
      toast.error("Impossible d'enregistrer l'état.");
    },
  });
  const onStateChange = (state: PatientEnCoursLineProps["state"]) => {
    if (state === "toClassify") {
      return;
    }
    patientStateMutation.mutate({
      patientId: props.patientId,
      stateId: props.stateId,
      state: {
        state,
      },
    });
  };
  const automaticState =
    props.state === "toClassify"
      ? props.next_rdv
        ? "apt_booked"
        : "must_call_back"
      : undefined;
  const onQuoteStateChange = (
    quoteState: PatientEnCoursLineProps["quoteState"] | undefined
  ) => {
    patientStateMutation.mutate({
      patientId: props.patientId,
      stateId: props.stateId,
      state: {
        quote_state: quoteState || null,
        ...(automaticState ? { state: automaticState } : {}),
      },
    });
  };
  const onPecStateChange = (
    pecState: PatientEnCoursLineProps["pecState"] | undefined
  ) => {
    patientStateMutation.mutate({
      patientId: props.patientId,
      stateId: props.stateId,
      state: {
        pec_state: pecState || null,
        ...(automaticState ? { state: automaticState } : {}),
      },
    });
  };
  // const onCommentSubmit = () => {
  //   patientStateMutation.mutate({
  //     patientId: props.patientId,
  //     stateId: props.stateId,
  //     state: {
  //       comment,
  //     },
  //   });
  // };

  const handlePeekComment = (e: React.MouseEvent) => {
    const container = e.currentTarget;
    const containerPos = container.getBoundingClientRect();
    props.onPeekComment({ x: containerPos.x, y: containerPos.y });
  };

  const handleClickEditComment = (e: React.MouseEvent) => {
    const container = e.currentTarget;
    const containerPos = container.getBoundingClientRect();
    props.onEditComment({ x: containerPos.x, y: containerPos.y });
  };

  const handleOpenPatientFile = () => {
    openPatientFile({
      patientId: props.patientId,
      allowSingleWindow: preferences?.single_patient_file_window ?? true,
    });
  };

  const hasTodo = props.pecState === "todo" || props.quoteState === "todo";
  const hasWaiting = props.pecState === "waiting_for_answer";
  return (
    <div
      className={classNames([
        "contents",
        props.rowBackgroundClassName || "",
        hasTodo ? "*:bg-red-300" : hasWaiting ? "*:bg-sky-200" : "",
      ])}
    >
      <button
        type="button"
        className="col-span-2 flex h-full items-center px-5 text-left cursor-pointer hover:text-blue-600"
        onClick={handleOpenPatientFile}
      >
        <span className="font-bold uppercase">{props.nom}</span>
        <span className="capitalize ml-2">{props.prenom}</span>
      </button>
      <div className="flex items-center gap-2 h-full">
        <Select3
          keyMap={stateMap}
          onSelect={onStateChange}
          defaultKey="toClassify"
          selected={props.state}
          disabled={patientStateMutation.isPending}
          className=""
        />
      </div>
      <div className="col-span2 h-full flex items-center px-1">
        {props.updated_at &&
          `Depuis ${DateTime.fromJSDate(props.updated_at)
            .setLocale("fr")
            .toFormat("ccc dd LLL yyyy")}`}
      </div>
      <div className="flex items-center gap-1 h-full px-2">
        Devis{" "}
        <Select3
          keyMap={quoteMap}
          onSelect={onQuoteStateChange}
          nullDisplay="Sans Objet"
          selected={props.quoteState || null}
          disabled={patientStateMutation.isPending}
          className=""
        />
      </div>
      <div className="flex  items-center h-full gap-2 px-1">
        PEC
        <Select3
          keyMap={pecMap}
          onSelect={onPecStateChange}
          nullDisplay="Sans Objet"
          selected={props.pecState || null}
          disabled={patientStateMutation.isPending}
          className=""
        />
      </div>
      <div className="col-span-2 h-full flex items-center px-1">
        {props.next_rdv ? (
          <>
            Rdv le{" "}
            {DateTime.fromJSDate(props.next_rdv.date)
              .setLocale("fr")
              .toFormat("ccc dd LLL")}{" "}
          </>
        ) : (
          <>Pas de rdv programmé</>
        )}
      </div>

      <div className="flex col-span-3 items-center h-full px-1 gap-2">
        <Button
          title="Commentaires"
          size="small"
          style={props.comment ? "main" : "secondary"}
          onClick={handleClickEditComment}
          disabled={patientStateMutation.isPending}
        />
        <div
          className="w-24 flex-1 overflow-hidden text-ellipsis whitespace-nowrap cursor-default hover:bg-gray-200 px-1"
          onMouseOver={handlePeekComment}
          onMouseLeave={props.onHideComment}
        >
          {props.comment}
        </div>
      </div>
    </div>
  );
};
export default PatientEnCoursLine;
